import { Col, Row } from 'antd';
import {
  addDays,
  format,
  hoursToMilliseconds,
  isEqual,
  isToday,
  isWeekend,
} from 'date-fns';
import {
  rosterLongHeaderDateFormat,
  shiftDateDataIndexFormat,
} from '../../../lib/utils/date-time';
import type {
  ColumnHeaderProps,
  IdentifiedShiftPeriod,
  RoleInformation,
  RosterCellValue,
  RosterColumnHeader,
  RosterTableRowRecord,
  RosterUserRecord,
  Shift,
  ShiftPeriod,
  ShiftPeriodLengths,
  ShiftPeriodType,
} from '../types';
import { recurrenceEndDateDefaultValue } from '../types';
import { Link } from 'react-router-dom';
import routes, { routesWithPathParameter } from '../../App/routes';
import {
  defaultRoleSubPriority,
  rolePriorities,
  roleSortPriority,
} from './prioritised-roles';
import type { DerivedTeamRoster } from '../../../hooks/types';
import { millisecondsPerDay } from './constants';
import type { RRule } from 'rrule';
import { localDateFromRRuleDate } from './RRule';
import { isDefined } from '../../../lib/utils';
import { archiveStatusSchema } from '../../Teams/types';
import { relatedNotesToRelatedNotesWithCreatedAtAndUpdatedAtAsNumber } from '../../Notes/util';

/**
 * The raw GraphQL type of RoleRoster that is returned from an API query
 */
type GraphQlRoleRoster = DerivedTeamRoster['roleRosters'][0];
type GraphQlShift = GraphQlRoleRoster['users'][0]['shifts'][0];

export const roleColumnDataIndex = 'firstColumnData';

// Antd options of Radio control does not accept readonly types.
export const rosterDisplayModes: readonly {
  readonly label: string;
  readonly value: string;
}[] = [
  { label: '1 Week', value: 'weekly' },
  { label: '4 Weeks', value: 'monthly' },
];

const numberOfDaysInWeek = 7;

/**
 * Dates in the week of the given date.
 */
export const daysInWeek = (startDate: Date) =>
  new Array(numberOfDaysInWeek)
    .fill(undefined)
    .map((_, i) => addDays(startDate, i));

/**
 * Dates in the month of the given date.
 */
export const daysInWeeks = (startDate: Date, numberOfWeek: number) => {
  return new Array(numberOfWeek * numberOfDaysInWeek)
    .fill(undefined)
    .map((_, i) => addDays(startDate, i));
};

/**
 * Roster table's column title based on the display mode.
 * Used to make sure the column title is fit when in monthly display mode.
 */
export const columnTitle = (date: Date) => (
  <>{format(date, rosterLongHeaderDateFormat)}</>
);

/**
 * Roster date title based on the display mode and anchor date.
 */
export const rosterDateTitle = (rosterDisplayMode: string, anchorDate: Date) =>
  rosterDisplayMode === 'weekly'
    ? `${format(anchorDate, 'dd/MM/yyyy')} - ${format(
        addDays(anchorDate, numberOfDaysInWeek - 1),
        'dd/MM/yyyy'
      )}`
    : format(anchorDate, 'yyyy');

/**
 * Produces a map for the user's shifts and the date of the shifts.
 * E.g. { [20220725]: [shift1, shift2] } where both shift1 and shift2 starts on the same day.
 */
const userShiftsDateMap = (shifts: readonly Shift[]) =>
  shifts.reduce<Record<string, readonly Shift[]>>((acc, shift) => {
    const dateColumnDataIndex = format(
      new Date(shift.startTime),
      shiftDateDataIndexFormat
    );

    return {
      ...acc,
      [dateColumnDataIndex]: [...(acc[dateColumnDataIndex] ?? []), shift],
    };
  }, {});

/**
 * Table record in the format of Antd Table component.
 * Represents the records that will be rendered for each row.
 * Each row will receive one record, and each column of the row will render the data based on the data index.
 * Data index will be the date (column title) for each record. E.g. { 20220725: [shift] }.
 *
 * If a personal roster, shifts are shown in the primary row (role title);
 * if a team roster, shifts are shown in the child rows (entitled by the user's name).
 * @param teamRoster roster of the team.
 * @param isPersonnelRoster if this is a personal roster.
 * @param teamRoleGroups role groups information.
 * @returns records to be rendered on roster table for shifts.
 */
export const shiftRecords = (
  teamRoster: DerivedTeamRoster,
  isPersonnelRoster: boolean,
  teamRoleGroups: readonly RoleInformation[]
): readonly RosterTableRowRecord[] =>
  teamRoster.roleRosters.map(
    (roster: GraphQlRoleRoster): RosterTableRowRecord => {
      const shiftRoleGroup: RoleInformation = teamRoleGroups.find(
        (group) => group.teamRoleId === roster.roleId
      ) ?? {
        roleGroup: 'Other',
        colour: 'white',
        name: 'Other',
        teamRoleId: '',
        roleGroupPriority: 0,
      };

      const shiftRoleGroupPriority = shiftRoleGroup.roleGroupPriority;

      const shiftRolePriority = rolePriorities.find(
        (p) => p.role === roster.roleName
      );

      const shiftRoleSubPriority =
        shiftRolePriority?.subPriority ?? defaultRoleSubPriority;

      const shiftRoleSortPriority = roleSortPriority(
        shiftRoleGroupPriority,
        shiftRoleSubPriority
      );

      const personalRosterUser = roster.users[0];

      const personnelRosterMode =
        isPersonnelRoster &&
        personalRosterUser?.userRoleAssignmentId !== undefined;

      const mapNotes = (shift: GraphQlShift) => ({
        ...shift,
        relatedNotes:
          relatedNotesToRelatedNotesWithCreatedAtAndUpdatedAtAsNumber(
            shift.relatedNotes
          ),
      });

      const userData: {
        readonly users: readonly RosterUserRecord[];
      } = personnelRosterMode
        ? {
            users: [
              {
                ...userShiftsDateMap(personalRosterUser.shifts.map(mapNotes)),
                key: `personal-roster-${roster.roleId}`,
                userId: personalRosterUser.userId,
                firstName: personalRosterUser.firstName,
                lastName: personalRosterUser.lastName,
                user_name: personalRosterUser.username,
                user_type: personalRosterUser.userType,
                roleName: roster.roleName,
                userRoleAssignmentId: personalRosterUser.userRoleAssignmentId,
                historic: personalRosterUser.historic,
              },
            ],
          }
        : {
            users: roster.users.map(
              (
                {
                  userId,
                  username,
                  firstName,
                  lastName,
                  userType,
                  shifts,
                  userRoleAssignmentId,
                  historic,
                },
                index
              ) => ({
                ...userShiftsDateMap(
                  shifts.map((shift) => ({
                    ...shift,
                    relatedNotes:
                      relatedNotesToRelatedNotesWithCreatedAtAndUpdatedAtAsNumber(
                        shift.relatedNotes
                      ),
                  }))
                ),
                key: `user-roster-${index}-${roster.roleId}`,
                userId,
                firstName,
                lastName,
                userRoleAssignmentId,
                user_name: username,
                user_type: userType,
                roleName: roster.roleName,
                historic,
              })
            ),
          };
      return {
        ...shiftRoleGroup,
        ...userData,
        key: `${roster.roleId}`,
        roleName: roster.roleName,
        rolePriority: shiftRoleSortPriority,
        firstColumnData: roster.roleName,
        teamRoleAssignmentId: roster.teamRoleAssignmentId,
        vacantShifts: userShiftsDateMap(
          roster.vacantShifts.map((shift) => ({
            ...shift,
            relatedNotes:
              relatedNotesToRelatedNotesWithCreatedAtAndUpdatedAtAsNumber(
                shift.relatedNotes
              ),
          }))
        ),
      };
    }
  );

/**
 * Returns the column headers for each roster table.
 * When multiple tables are being rendered, dates headings will only be shown on the first table.
 * If teamName is given, it will be displayed in the upper left column
 * If only a single user, the team buttons won't be shown.
 */
export const columnHeaders = ({
  teamId,
  rosterDates,
  personalRosterMode,
  teamName,
  isArchived,
}: ColumnHeaderProps): readonly RosterColumnHeader[] => {
  const appendStatus = isArchived
    ? ` (${archiveStatusSchema.Values.Archived})`
    : '';
  const teamNameLink =
    teamName !== undefined ? `${teamName}${appendStatus}` : '';

  // For single users, the tables are stacked; each roster has a row of dates.
  // A future improvement may be to sync the scrollbars together.
  const firstColumnHeader: RosterColumnHeader = {
    title: (
      <Row wrap={false}>
        {personalRosterMode ? (
          <Col flex="auto">Assigned Roles</Col>
        ) : (
          <Col style={{ alignSelf: 'center' }}>{`Role & Personnel`}</Col>
        )}
      </Row>
    ),
    link: (
      <Col flex="auto">
        <Link
          to={{
            pathname: routesWithPathParameter(
              routes.teamRoster,
              'teamId',
              teamId
            ),
          }}
          style={{ textDecoration: 'underline' }}
        >
          {teamNameLink}
        </Link>
      </Col>
    ),
    dataIndex: roleColumnDataIndex,
    isToday: false,
    isWeekend: false,
    isLastColumn: false,
  };

  return [firstColumnHeader].concat(
    rosterDates.map((date, i, { length }) => ({
      title: columnTitle(date),
      isToday: isToday(date),
      isWeekend: isWeekend(date),
      dataIndex: format(date, shiftDateDataIndexFormat),
      isLastColumn: i + 1 === length,
      link: <></>,
    }))
  );
};

/**
 * Splices a Shift into day and night periods, according to the provided 'day shift' time period.
 * @param dayShiftPeriod The designated 'day shift' time period
 * @param shiftPeriod The shift start and end time period
 * @returns The shift, spliced into day-night periods
 */
export const splicedShift = (
  dayShiftPeriod: ShiftPeriod,
  shiftPeriod: ShiftPeriod
): readonly IdentifiedShiftPeriod[] => {
  const dayShiftLength = dayShiftPeriod.end - dayShiftPeriod.start;

  const shiftLength = shiftPeriod.end - shiftPeriod.start;

  const shiftPeriodLengths = {
    day: dayShiftLength,
    night: millisecondsPerDay - dayShiftLength,
  };

  const firstPeriod: IdentifiedShiftPeriod = firstPeriodLengthAndTime(
    dayShiftPeriod,
    shiftPeriod
  );

  const remainingShiftTime = shiftLength - firstPeriod.length;

  if (remainingShiftTime === 0) {
    return [firstPeriod];
  }

  const nextShiftPeriodIsDay = !firstPeriod.isDayShift;

  const initialIdentifiedPeriods: readonly IdentifiedShiftPeriod[] = [
    firstPeriod,
  ];

  return nextShiftPeriods(
    remainingShiftTime,
    nextShiftPeriodIsDay,
    shiftPeriodLengths,
    initialIdentifiedPeriods
  );
};

/**
 * Identifies the length and kind of the first portion of a shift
 * @param dayShiftPeriod The designated 'day shift' time period
 * @param shiftPeriod the two numbers (dates) that define the start and end of a shift
 * @returns The first period (day or night) in the shift
 */
export const firstPeriodLengthAndTime = (
  dayShiftPeriod: ShiftPeriod,
  shiftPeriod: ShiftPeriod
): IdentifiedShiftPeriod => {
  const thisShiftPeriod = shiftPeriodType(shiftPeriod.start, dayShiftPeriod);

  const startTimeOfNextPeriod =
    thisShiftPeriod === 'early'
      ? dayShiftPeriod.start
      : thisShiftPeriod === 'day'
        ? dayShiftPeriod.end
        : dayShiftPeriod.start + hoursToMilliseconds(24);

  const timeUntilNextPeriodStarts = startTimeOfNextPeriod - shiftPeriod.start;

  const shiftLength = shiftPeriod.end - shiftPeriod.start;

  const firstPeriodLength =
    timeUntilNextPeriodStarts < shiftLength
      ? timeUntilNextPeriodStarts
      : shiftLength;

  return {
    length: firstPeriodLength,
    isDayShift: thisShiftPeriod === 'day',
  };
};

/**
 * Separates a shift into periods
 * @param remainingShiftTime The amount of total remaining time in the shift
 * @param nextPeriodIsDay Whether the next period is day
 * @param shiftPeriodLengths The lengths of the shift periods
 * @param separatedPeriods Shift periods that have already been identified
 * @returns An array of identified shift periods
 */
export const nextShiftPeriods = (
  remainingShiftTime: number,
  nextPeriodIsDay: boolean,
  shiftPeriodLengths: ShiftPeriodLengths,
  separatedPeriods: readonly IdentifiedShiftPeriod[]
): readonly IdentifiedShiftPeriod[] => {
  const dayOrNight = nextPeriodIsDay ? 'day' : 'night';

  const nextPeriodLength =
    remainingShiftTime <= shiftPeriodLengths[dayOrNight]
      ? remainingShiftTime
      : shiftPeriodLengths[dayOrNight];

  const totalRemainingTime = remainingShiftTime - nextPeriodLength;

  const nextPeriod = {
    length: nextPeriodLength,
    isDayShift: nextPeriodIsDay,
  };

  const updatedSeparatedPeriods: readonly IdentifiedShiftPeriod[] = [
    ...separatedPeriods,
    nextPeriod,
  ];

  if (totalRemainingTime === 0) {
    return updatedSeparatedPeriods;
  }

  return nextShiftPeriods(
    totalRemainingTime,
    !nextPeriodIsDay,
    shiftPeriodLengths,
    updatedSeparatedPeriods
  );
};

/**
 * Given a time (number of milliseconds through the day) and a designated day shift period,
 * returns the period of day within which the time sits.
 * @param time The time at which a shift starts
 * @param dayShiftPeriod The designated 'day shift' time period
 * @returns Whether the period is early, day, or late
 */
export const shiftPeriodType = (
  time: number,
  dayShiftPeriod: ShiftPeriod
): ShiftPeriodType => {
  if (time < dayShiftPeriod.start) {
    return 'early';
  }
  if (time >= dayShiftPeriod.end) {
    return 'late';
  }
  return 'day';
};
/**
 * Type guard to check if a cell value is a Shift data or string value.
 * Used to determine how to render cell content, since the cell rendering will get field data based on data index.
 */
export const isShiftCell = (
  value?: RosterCellValue
): value is readonly Shift[] | undefined => typeof value !== 'string';

export const determineInitialRecurEndDate = (
  initialShiftRecurRule: RRule,
  shift: Shift | undefined
): Date | null => {
  // The RRule type for this date can come through as undefined, even if the types don't say so
  // The null-coalescing operator will convert nulls to undefined so we don't have to check for two types
  const rRuleEndDate = initialShiftRecurRule.options.until ?? undefined;
  const recurrenceEndDate = shift?.recurrenceEndDate;

  const endDateFromRRule =
    rRuleEndDate !== undefined
      ? localDateFromRRuleDate(rRuleEndDate)
      : undefined;
  const endDateFromShiftData =
    recurrenceEndDate !== undefined &&
    !isEqual(
      new Date(recurrenceEndDate),
      new Date(recurrenceEndDateDefaultValue)
    )
      ? new Date(recurrenceEndDate)
      : undefined;

  const minimumEndDate: number | undefined = [
    endDateFromRRule,
    endDateFromShiftData,
  ]
    .filter(isDefined)
    .reduce<number | undefined>(
      (prev, curr) => Math.min(prev ?? Number.MAX_VALUE, curr.getTime()),
      undefined
    );

  if (minimumEndDate !== undefined) {
    return new Date(minimumEndDate);
  }
  return null;
};

/**
 * Makes an inclusive numerical range between two numbers.
 * The range will only be generated if `from` is <= `to`.
 * @param from - The start of the range
 * @param to - The end of the range
 * @returns An array of numbers between the start and end, inclusive
 */
export const rangeInclusive: (from: number, to: number) => readonly number[] = (
  from,
  to
) => Array.from({ length: to - (from - 1) }, (_, i) => i + from);
